import { useRef, useState } from "react";
import { resolveIcon } from "@/lib/mapIcons";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function resolveAsset(url) {
    if (!url) return "";
    if (/^https?:\/\//i.test(url)) return url;
    if (url.startsWith("/api/")) return `${BACKEND_URL}${url}`;
    return url;
}

/**
 * Editable map preview used in admin panel.
 *
 * Props:
 *  - imageUrl, imageWebpUrl
 *  - pois, buildings, zones, paths (arrays from API)
 *  - mode: "poi" | "building" | "zone" | "path" – determines what clicks do
 *  - draftPosition: { left, top } | null – currently-placed marker for POI/Building
 *  - draftZone: { left, top, width, height } | null
 *  - draftPathPoints: [{left, top}, ...]
 *  - onClickMap: (pos:{left, top}) => void  (called when user clicks empty map area)
 *  - onSelectExisting: (type, item) => void
 *  - highlightId: string | null (id of item currently being edited)
 */
export default function MapPreview({
    imageUrl,
    imageWebpUrl,
    pois = [],
    buildings = [],
    zones = [],
    paths = [],
    mode = "poi",
    draftPosition = null,
    draftZone = null,
    draftPathPoints = [],
    onClickMap,
    onSelectExisting,
    highlightId = null,
}) {
    const imgRef = useRef(null);
    const [hoverPos, setHoverPos] = useState(null);

    const handleClick = (e) => {
        if (!onClickMap) return;
        const img = imgRef.current;
        if (!img) return;
        const rect = img.getBoundingClientRect();
        const left = ((e.clientX - rect.left) / rect.width) * 100;
        const top = ((e.clientY - rect.top) / rect.height) * 100;
        if (left < 0 || left > 100 || top < 0 || top > 100) return;
        onClickMap({ left: +left.toFixed(2), top: +top.toFixed(2) });
    };

    const handleMove = (e) => {
        const img = imgRef.current;
        if (!img) return;
        const rect = img.getBoundingClientRect();
        const left = ((e.clientX - rect.left) / rect.width) * 100;
        const top = ((e.clientY - rect.top) / rect.height) * 100;
        setHoverPos({ left, top });
    };

    return (
        <div className="relative w-full rounded-2xl border border-gray-200 overflow-hidden bg-gray-50 select-none">
            <div
                onClick={handleClick}
                onMouseMove={handleMove}
                onMouseLeave={() => setHoverPos(null)}
                className="relative cursor-crosshair"
                data-testid="map-preview-canvas"
            >
                <picture className="block">
                    {imageWebpUrl ? <source srcSet={resolveAsset(imageWebpUrl)} type="image/webp" /> : null}
                    <img
                        ref={imgRef}
                        src={resolveAsset(imageUrl || "/mapa-osiedla.jpg")}
                        alt="Mapa osiedla"
                        className="block w-full h-auto pointer-events-none"
                        draggable={false}
                    />
                </picture>

                {/* Zones */}
                {zones.map((z) => (
                    <button
                        type="button"
                        key={z.id}
                        onClick={(e) => { e.stopPropagation(); onSelectExisting && onSelectExisting("zone", z); }}
                        className={`absolute rounded-xl transition-all ${highlightId === z.id ? "ring-2 ring-[#D4A537]" : ""}`}
                        style={{
                            left: `${z.left}%`, top: `${z.top}%`,
                            width: `${z.width}%`, height: `${z.height}%`,
                            backgroundColor: z.color, opacity: z.opacity,
                            border: z.border_color ? `2px solid ${z.border_color}` : "1px dashed rgba(0,0,0,0.3)",
                        }}
                        title={z.label}
                    />
                ))}

                {/* Draft zone preview */}
                {draftZone && (
                    <div
                        className="absolute rounded-xl border-2 border-dashed border-[#D4A537] bg-[#D4A537]/20 pointer-events-none"
                        style={{
                            left: `${draftZone.left}%`, top: `${draftZone.top}%`,
                            width: `${draftZone.width}%`, height: `${draftZone.height}%`,
                        }}
                    />
                )}

                {/* Paths */}
                {paths.length > 0 && (
                    <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 100 100" preserveAspectRatio="none" style={{ overflow: "visible" }}>
                        <defs>
                            {paths.map((pa) => (
                                <marker key={`adm-arrow-${pa.id}`} id={`adm-arrow-${pa.id}`} viewBox="0 0 10 10" refX="8" refY="5" markerWidth="4" markerHeight="4" orient="auto-start-reverse">
                                    <path d="M 0 0 L 10 5 L 0 10 z" fill={pa.color} />
                                </marker>
                            ))}
                        </defs>
                        {paths.map((pa) => (
                            <polyline
                                key={pa.id}
                                points={(pa.points || []).map((p) => `${p.left},${p.top}`).join(" ")}
                                fill="none"
                                stroke={pa.color}
                                strokeWidth={(pa.stroke_width || 3) / 5}
                                strokeLinejoin="round"
                                strokeLinecap="round"
                                strokeDasharray={pa.dash ? "1.2,0.8" : undefined}
                                markerEnd={pa.has_arrow ? `url(#adm-arrow-${pa.id})` : undefined}
                                vectorEffect="non-scaling-stroke"
                                opacity={highlightId === pa.id ? 1 : 0.6}
                            />
                        ))}
                    </svg>
                )}

                {/* Draft path waypoints */}
                {draftPathPoints.length > 0 && (
                    <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 100 100" preserveAspectRatio="none" style={{ overflow: "visible" }}>
                        <polyline
                            points={draftPathPoints.map((p) => `${p.left},${p.top}`).join(" ")}
                            fill="none"
                            stroke="#D4A537"
                            strokeWidth="0.6"
                            strokeDasharray="1.5,1"
                            strokeLinejoin="round"
                            vectorEffect="non-scaling-stroke"
                        />
                    </svg>
                )}
                {draftPathPoints.map((p, i) => (
                    <div
                        key={`dpt-${i}`}
                        className="absolute -translate-x-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-[#D4A537] ring-2 ring-white pointer-events-none"
                        style={{ left: `${p.left}%`, top: `${p.top}%` }}
                    />
                ))}

                {/* Buildings */}
                {buildings.map((b) => (
                    <button
                        type="button"
                        key={b.id}
                        onClick={(e) => { e.stopPropagation(); onSelectExisting && onSelectExisting("building", b); }}
                        style={{ left: `${b.left}%`, top: `${b.top}%` }}
                        className={`absolute -translate-x-1/2 -translate-y-1/2 z-10 px-2 py-0.5 text-[10px] font-bold rounded shadow ${
                            highlightId === b.id ? "bg-[#D4A537] text-[#092E22] ring-2 ring-white" : "bg-[#092E22] text-white hover:bg-[#D4A537] hover:text-[#092E22]"
                        }`}
                        title={b.label}
                    >
                        {b.building_id}
                    </button>
                ))}

                {/* POIs */}
                {pois.map((p) => {
                    const Icon = resolveIcon(p.icon);
                    return (
                        <button
                            type="button"
                            key={p.id}
                            onClick={(e) => { e.stopPropagation(); onSelectExisting && onSelectExisting("poi", p); }}
                            style={{ left: `${p.left}%`, top: `${p.top}%`, backgroundColor: p.color }}
                            className={`absolute -translate-x-1/2 -translate-y-1/2 z-20 w-7 h-7 rounded-full flex items-center justify-center ring-2 shadow transition-all hover:scale-110 ${
                                highlightId === p.id ? "ring-[#D4A537] ring-4" : "ring-white"
                            } ${p.visible === false ? "opacity-40" : ""}`}
                            title={p.label}
                        >
                            <Icon size={12} className="text-white" strokeWidth={2.5} />
                        </button>
                    );
                })}

                {/* Draft marker for POI/Building modes */}
                {draftPosition && (mode === "poi" || mode === "building") && (
                    <div
                        className="absolute -translate-x-1/2 -translate-y-1/2 z-30 pointer-events-none"
                        style={{ left: `${draftPosition.left}%`, top: `${draftPosition.top}%` }}
                    >
                        <div className="w-7 h-7 rounded-full bg-[#D4A537] ring-4 ring-[#D4A537]/40 animate-pulse" />
                    </div>
                )}

                {/* Crosshair hover */}
                {hoverPos && (mode === "poi" || mode === "building" || mode === "path") && (
                    <div
                        className="absolute -translate-x-1/2 -translate-y-1/2 z-40 pointer-events-none"
                        style={{ left: `${hoverPos.left}%`, top: `${hoverPos.top}%` }}
                    >
                        <div className="w-3 h-3 rounded-full bg-white/80 ring-1 ring-black/40" />
                    </div>
                )}
            </div>

            {/* Status bar */}
            <div className="absolute bottom-2 right-2 px-2 py-1 rounded bg-black/60 text-white text-[10px] font-mono pointer-events-none">
                {hoverPos
                    ? `left: ${hoverPos.left.toFixed(1)}%  top: ${hoverPos.top.toFixed(1)}%`
                    : "Najedź lub kliknij na mapę"}
            </div>
        </div>
    );
}
